import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useInfiniteProjects } from '../../hooks/useInfiniteProjects';
import { useTableSelection } from '../../hooks/useTableSelection';
import { useTableRowNavigation } from '../../hooks/useTableRowNavigation';
import { projectsService, paymentsService, leadsService } from '../../services/supabase';
import { useI18n } from '../../context/I18nContext';
import { useToast } from '../../context/ToastContext';
import { useConfirmModal } from '../../context/ConfirmModalContext';
import { useTranslations } from '../../hooks/useTranslations';
import { Lead, Project, ProjectStatus, Payment } from '../../types';
import ProjectModal from '../../components/projects/ProjectModal';
import DiffusionStartedModal from '../../components/projects/DiffusionStartedModal';
import MarkAsPaidModal from '../../components/payments/MarkAsPaidModal';
import { useCreateLeadFromSearch } from '../../hooks/useCreateLeadFromSearch';
import { upsertById } from '../../utils/upsertById';
import { revenueAmountForMarkAsPaid } from '../../utils/paymentRevenue';
import KanbanBoard from '../../components/projects/KanbanBoard';
import KanbanCompactNav, { KANBAN_ACTIVE_STATUSES } from '../../components/projects/KanbanCompactNav';
import { computeKanbanLayout, useMeasuredWidth } from '../../components/projects/kanbanLayout';
import TableSection from '../../components/ui/TableSection';
import TableFilters, { ActiveFilter, FilterFieldDef } from '../../components/ui/TableFilters';
import { matchFilterValue } from '../../utils/filterUtils';
import Card from '../../components/ui/Card';
import PageHeader from '../../components/ui/PageHeader';
import { PAGE_GAP } from '../../styles/pageLayout';
import Table, {
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableSelectionHeader,
  TableSelectionCell,
  TableActionsHeader,
  TableActionsCell,
  tableRowSelectedClass,
} from '../../components/ui/Table';
import ActionIconButton from '../../components/ui/ActionIconButton';
import Button from '../../components/ui/Button';
import ContextMenu, { type ContextMenuItem } from '../../components/ui/ContextMenu';
import { TABLE_QUICK_ACTION_CLASS, appendEditDeleteMenuItems, useQuickEditDeleteVisible } from '../../components/ui/tableActions';
import { ChevronLeft, Archive, Edit, Trash2, LayoutGrid, Plus } from 'lucide-react';
import InfiniteLoadMore from '../../components/ui/InfiniteLoadMore';
import { exportToCSV, formatDateTimeForExport } from '../../utils/exportUtils';
import Spinner from '../../components/ui/Spinner';
import { highlightRing } from '../../styles/colors';
import BulkActionsBar from '../../components/ui/BulkActionsBar';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { useBulkTableActions } from '../../hooks/useBulkTableActions';
import { isOptimisticId } from '../../utils/optimisticId';
import { addDurationToLocalDate, type DurationUnit } from '../../utils/rightsDuration';
import { PAYMENTS_DUE_STATUS_UPDATED, notifyPaymentMutated } from '../../hooks/usePaymentDueStatusSync';
import RightsEndStatus from '../../components/projects/RightsEndStatus';
import {
  captureProjectCreateDeepLink,
  clearProjectCreateDeepLink,
  peekProjectCreateDeepLink,
} from '../../utils/projectCreateDeepLink';
import { PAYMENT_UPDATED_EVENT } from '../../utils/paymentEvents';

const PROJECT_TYPE_ICONS: Record<string, string> = {
  short_video: '🎬',
  photo: '📸',
  voice_over: '🎙️',
  story: '📱',
  post: '🖼️',
  long_video: '🎥',
  other: '📄',
};

/** Colonne cliquable « Retour Kanban » : même hauteur que le contenu scrollable (liste archivée). */
const BackToKanbanColumn: React.FC<{
  activeCount: number;
  onClick: () => void;
  ariaLabel: string;
  className?: string;
}> = ({ activeCount, onClick, ariaLabel, className = '' }) => (
  <div
    className={`w-16 flex-shrink-0 relative flex flex-col cursor-pointer rounded-xl border shadow-sm bg-white/50 backdrop-blur-sm border-gray-200/60 transition-all hover:border-gray-300 hover:bg-white/70 hover:shadow-md group self-stretch ${className}`}
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
    aria-label={ariaLabel}
  >
    <div
      className="absolute inset-0 rounded-lg opacity-[0.08]"
      style={{
        backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(0, 0, 0, 0.1) 10px, rgba(0, 0, 0, 0.1) 20px)',
      }}
    />
    <div className="sticky top-0 z-10 bg-white rounded-t-xl shadow-sm flex-shrink-0">
      <div className="h-1 bg-gradient-to-r from-slate-400 to-gray-400 rounded-t-xl" />
      <div className="px-2 sm:px-3 py-2.5 sm:py-3.5 border-b border-gray-200/60">
        <div className="flex items-center justify-center">
          <span className="inline-flex items-center justify-center min-w-[20px] sm:min-w-[22px] h-5 sm:h-6 px-1 sm:px-1.5 text-xs font-semibold bg-slate-100/80 text-slate-700 rounded-full group-hover:opacity-80 transition-colors">
            {activeCount}
          </span>
        </div>
      </div>
    </div>
    <div className="flex-1 relative min-h-0 flex items-center justify-center">
      <div className="flex items-center justify-center space-x-1 sm:space-x-1.5 px-2 sm:px-3 py-2 sm:py-3">
        <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
        <LayoutGrid className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-400 group-hover:text-gray-600 transition-colors hidden sm:block" />
      </div>
    </div>
  </div>
);

const Projects: React.FC = () => {
  const { t } = useI18n();
  const { showToast, showToastWithUndo } = useToast();
  const { confirm } = useConfirmModal();
  const { requestCreateLead, leadCreateModal } = useCreateLeadFromSearch();
  const { formatDateShort, formatCurrency, formatDateLocale } = useTranslations();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const showQuickEditDelete = useQuickEditDeleteVisible();

  const handleCreateLeadFromSearch = useCallback(
    (args: Parameters<typeof requestCreateLead>[0]) => {
      requestCreateLead({
        prefillName: args.prefillName,
        onCreated: (lead) => {
          setLeads((prev) => upsertById(prev, lead));
          args.onCreated(lead);
        },
      });
    },
    [requestCreateLead]
  );

  // Paginated projects with optimistic CRUD - single source of truth
  const {
    projects: allProjects,
    loadingInitial: projectsLoading,
    refreshing: projectsRefreshing,
    error: projectsError,
    crud: projectsCRUD,
    addProject: addPaginatedProject,  // Low-level for undo
    hasMore,
    loadingMore,
    loadMore,
  } = useInfiniteProjects({
    limit: 50,
    onUpdateFailed: () => showToast(t('common.saveFailedRollback'), 'error'),
  }); // Notion-like progressive loading
  
  // Spinner only when list is empty (initial load or refresh). Rollback runs with items, so no blocking spinner.
  // When hydrating from cache we may momentarily have 0 items; don't show empty state then.
  const loading = projectsLoading || projectsRefreshing;
  const error = projectsError;

  // Leads: fetch only what we need for loaded projects (avoid leadsService.getAll)
  const [leads, setLeads] = useState<Lead[]>([]);
  const leadsKnownIdsRef = useRef<Set<string>>(new Set());
  const leadsLoadSeqRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const leadIds = Array.from(new Set(allProjects.map((p) => p.leadId).filter(Boolean)));
    const missing = leadIds.filter((id) => !leadsKnownIdsRef.current.has(id));
    if (missing.length === 0) return;

    const load = async () => {
      const seq = ++leadsLoadSeqRef.current;
      try {
        const chunkSize = 200;
        const chunks: string[][] = [];
        for (let i = 0; i < missing.length; i += chunkSize) {
          chunks.push(missing.slice(i, i + chunkSize));
        }

        const results: Lead[] = [];
        for (const chunk of chunks) {
          const part = await leadsService.getByIds(chunk);
          results.push(...part);
        }

        if (cancelled) return;
        if (seq !== leadsLoadSeqRef.current) return;

        missing.forEach((id) => leadsKnownIdsRef.current.add(id));
        setLeads((prev) => {
          const map = new Map<string, Lead>();
          prev.forEach((l) => map.set(l.id, l));
          results.forEach((l) => map.set(l.id, l));
          return Array.from(map.values());
        });
      } catch (e) {
        console.error('Error loading leads for projects:', e);
        missing.forEach((id) => leadsKnownIdsRef.current.add(id));
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [allProjects]);

  const leadsById = useMemo(() => new Map(leads.map((l) => [l.id, l])), [leads]);

  // Get payments separately
  const [payments, setPayments] = useState<Payment[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  /** Prefill lead when opening create from /app/projects?create=1&lead=… */
  const [createPrefillLead, setCreatePrefillLead] = useState<Lead | null>(null);
  /** Prefill status when creating from a Kanban column + button */
  const [createDefaultStatus, setCreateDefaultStatus] = useState<Project['status'] | null>(null);
  const [isMarkAsPaidModalOpen, setIsMarkAsPaidModalOpen] = useState(false);
  const [markingAsPaidPayment, setMarkingAsPaidPayment] = useState<Payment | null>(null);
  const [diffusionProject, setDiffusionProject] = useState<Project | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [view, setView] = useState<'kanban' | 'archived'>('kanban');
  /** When leaving archived via compact chips, focus this status on the board */
  const [compactFocusStatus, setCompactFocusStatus] = useState<ProjectStatus | null>(null);
  const { ref: projectsLayoutRef, width: projectsLayoutWidth } = useMeasuredWidth();
  const projectsLayout = useMemo(
    () => computeKanbanLayout(projectsLayoutWidth, { hasArchiveStrip: true }),
    [projectsLayoutWidth]
  );
  const archivedIsFullBoard = projectsLayout.isFullBoard;
  const archivedShowChipLabels = !archivedIsFullBoard && projectsLayout.visibleColumnCount >= 2;
  /** Phone kanban: page grows + main scrolls (no sticky chrome). */
  const kanbanPageScroll =
    view === 'kanban' &&
    !projectsLayout.isFullBoard &&
    projectsLayout.visibleColumnCount <= 1;
  
  // Archived view shows loaded items and can load more from server

  // Load payments (only for loaded projects)
  const paymentsLoadSeqRef = useRef(0);
  const paymentsKnownProjectIdsRef = useRef<Set<string>>(new Set());
  const paymentMutSeqRef = useRef<Record<string, number>>({});
  const [paymentsSyncTrigger, setPaymentsSyncTrigger] = useState(0);

  // Listen for payment due status sync (from Layout) to refresh payments
  useEffect(() => {
    const handler = () => {
      paymentsKnownProjectIdsRef.current = new Set();
      setPaymentsSyncTrigger((prev) => prev + 1);
    };
    window.addEventListener(PAYMENTS_DUE_STATUS_UPDATED, handler);
    return () => window.removeEventListener(PAYMENTS_DUE_STATUS_UPDATED, handler);
  }, []);

  useEffect(() => {
    const onExternalPaymentUpdate = (event: Event) => {
      const payment = (event as CustomEvent<Payment>).detail;
      if (!payment?.id) return;
      setPayments((prev) => prev.map((p) => (p.id === payment.id ? payment : p)));
    };
    window.addEventListener(PAYMENT_UPDATED_EVENT, onExternalPaymentUpdate);
    return () => window.removeEventListener(PAYMENT_UPDATED_EVENT, onExternalPaymentUpdate);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const projectIds = Array.from(new Set(allProjects.map((p) => p.id).filter(Boolean).filter((id) => !isOptimisticId(id))));
    const missing = projectIds.filter((id) => !paymentsKnownProjectIdsRef.current.has(id));
    if (missing.length === 0) return;

    const load = async () => {
      const seq = ++paymentsLoadSeqRef.current;
      try {
        const chunkSize = 200;
        const chunks: string[][] = [];
        for (let i = 0; i < missing.length; i += chunkSize) {
          chunks.push(missing.slice(i, i + chunkSize));
        }

        const results: Payment[] = [];
        for (const chunk of chunks) {
          const part = await paymentsService.getByProjectIds(chunk);
          results.push(...part);
        }

        if (cancelled) return;
        if (seq !== paymentsLoadSeqRef.current) return;

        missing.forEach((id) => paymentsKnownProjectIdsRef.current.add(id));
        setPayments((prev) => {
          const map = new Map<string, Payment>();
          prev.forEach((p) => map.set(p.id, p));
          results.forEach((p) => map.set(p.id, p));
          return Array.from(map.values());
        });
      } catch (err) {
        console.error('Error loading payments:', err);
        missing.forEach((id) => paymentsKnownProjectIdsRef.current.add(id));
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [allProjects, paymentsSyncTrigger]);

  // Handle URL parameter for project selection (scroll + highlight)
  const { highlightedItemId: highlightedProjectId } = useTableRowNavigation<Project, string, 'kanban' | 'archived'>({
    paramName: 'project',
    dataAttribute: 'data-project-id',
    allItems: allProjects,
    getId: (project) => project.id,
    viewConfig: {
      currentView: view,
      setView: (v) => setView(v),
      getRequiredView: (project) => project.status === 'ARCHIVED' ? 'archived' : null,
    },
    fetchItem: (id) => projectsService.getById(id),
  });

  const typeOptions = useMemo(
    () => [
      { value: '', label: t('projects.allTypes') },
      { value: 'short_video', label: t('projects.typeShortVideo') },
      { value: 'photo', label: t('projects.typePhoto') },
      { value: 'voice_over', label: t('projects.typeVoiceOver') },
      { value: 'story', label: t('projects.typeStory') },
      { value: 'post', label: t('projects.typePost') },
      { value: 'long_video', label: t('projects.typeLongVideo') },
      { value: 'other', label: t('projects.typeOther') },
    ],
    [t]
  );
  const getProjectTypeLabel = useCallback(
    (type?: string) => typeOptions.find((option) => option.value === type)?.label ?? type ?? '-',
    [typeOptions]
  );
  const statusProjectOptions = useMemo(
    () => [
      { value: '', label: t('common.allStatus') },
      { value: 'BRIEF_RECEIVED', label: t('projects.statusBriefReceived') },
      { value: 'IN_PRODUCTION', label: t('projects.statusInProduction') },
      { value: 'V1_SENT', label: t('projects.statusFirstVersion') },
      { value: 'CHANGES_REQUESTED', label: t('projects.statusCorrections') },
      { value: 'DELIVERED', label: t('projects.statusDelivered') },
      { value: 'ARCHIVED', label: t('projects.statusArchived') },
    ],
    [t]
  );
  const projectFilterFields: FilterFieldDef[] = useMemo(
    () => [
      { id: 'name', type: 'text', label: t('projects.name'), placeholder: t('common.search') },
      { id: 'lead', type: 'text', label: t('projects.lead'), placeholder: t('common.search') },
      { id: 'type', type: 'select', label: t('projects.type'), options: typeOptions },
      { id: 'status', type: 'select', label: t('projects.status'), options: statusProjectOptions },
      { id: 'amount', type: 'number', label: t('projects.amount'), placeholder: '0' },
      { id: 'deadline', type: 'date', label: t('projects.deadline'), placeholder: '' },
      { id: 'brief', type: 'text', label: t('projects.description'), placeholder: t('common.search') },
    ],
    [t, typeOptions, statusProjectOptions]
  );

  // Apply a single active filter (uses shared filterUtils for number/date/text/select)
  const projectMatchesFilter = useCallback(
    (project: Project, lead: Lead | undefined, f: ActiveFilter, fields: FilterFieldDef[]): boolean => {
      const fieldDef = fields.find((x) => x.id === f.fieldId);
      if (!fieldDef) return true;
      const getCellValue = (): string | number | null | undefined => {
        switch (f.fieldId) {
          case 'name': return project.name ?? '';
          case 'lead': return lead?.name ?? '';
          case 'type': return project.type ?? '';
          case 'status': return project.status ?? '';
          case 'brief': return project.brief ?? '';
          case 'amount': return project.amount ?? null;
          case 'deadline': return project.deadline != null ? String(project.deadline) : null;
          default: return '';
        }
      };
      return matchFilterValue(fieldDef.type, f.operator, f.value ?? '', getCellValue());
    },
    []
  );

  // Project filtering (search bar + all active filters)
  const filteredProjects = useMemo(() => {
    return allProjects.filter(project => {
      const lead = project.leadId ? leadsById.get(project.leadId) : undefined;
      const matchesSearch =
        !searchTerm ||
        project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (lead?.name ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (project.brief ?? '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilters = activeFilters.every((f) => projectMatchesFilter(project, lead, f, projectFilterFields));
      const isNotArchived = project.status !== 'ARCHIVED';
      return matchesSearch && matchesFilters && isNotArchived;
    });
  }, [allProjects, leadsById, searchTerm, activeFilters, projectMatchesFilter, projectFilterFields]);

  // Separate archived projects
  const archivedProjects = useMemo(() => {
    return allProjects
      .filter(project => {
        const lead = project.leadId ? leadsById.get(project.leadId) : undefined;
        const matchesSearch =
          !searchTerm ||
          project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (lead?.name ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (project.brief ?? '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilters = activeFilters.every((f) => projectMatchesFilter(project, lead, f, projectFilterFields));
        const isArchived = project.status === 'ARCHIVED';
        return matchesSearch && matchesFilters && isArchived;
      })
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [allProjects, leadsById, searchTerm, activeFilters, projectMatchesFilter, projectFilterFields]);

  /** Bulk actions for archived projects table (same pattern as Leads/Finances). */
  const archivedBulk = useBulkTableActions<Project>({
    items: archivedProjects,
    onBulkDeleteConfirm: (ids) => {
      const idSet = new Set(ids);
      const removedPayments = payments.filter((p) => idSet.has(p.projectId));
      projectsCRUD.bulkRemove(ids);
      if (removedPayments.length > 0) {
        setPayments((prev) => prev.filter((p) => !idSet.has(p.projectId)));
        notifyPaymentMutated();
      }
    },
  });

  const bulkDeleteLinkedPaymentCount = useMemo(() => {
    if (!archivedBulk.bulkDeleteModal.isOpen) return 0;
    const ids = new Set(archivedBulk.selectedItems.map((p) => p.id));
    return payments.filter((p) => ids.has(p.projectId)).length;
  }, [archivedBulk.bulkDeleteModal.isOpen, archivedBulk.selectedItems, payments]);

  const archivedSelection = useTableSelection<Project>({
    items: archivedProjects,
    ...archivedBulk.tableProps,
  });

  // Archived projects list (server can load more via cursor pagination)

  // Sort by creation date (newest first)
  const sortedProjects = useMemo(() => {
    return [...filteredProjects].sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [filteredProjects]);

  const kanbanStatusCounts = useMemo(() => {
    const counts: Partial<Record<ProjectStatus, number>> = {};
    KANBAN_ACTIVE_STATUSES.forEach((s) => {
      counts[s] = 0;
    });
    sortedProjects.forEach((p) => {
      if (counts[p.status] != null) counts[p.status]! += 1;
    });
    return counts;
  }, [sortedProjects]);

  // Removed overview-only stats on this page

  const handleOpenModal = (project?: Project) => {
    setCreatePrefillLead(null);
    setCreateDefaultStatus(null);
    setEditingProject(project || null);
    setIsModalOpen(true);
  };

  const handleCreateInColumn = (status: Project['status']) => {
    setCreatePrefillLead(null);
    setEditingProject(null);
    setCreateDefaultStatus(status);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingProject(null);
    setCreatePrefillLead(null);
    setCreateDefaultStatus(null);
  };

  // Dashboard / Leads → /app/projects?create=1[&lead=…]
  useEffect(() => {
    if (searchParams.get('create') === '1') {
      captureProjectCreateDeepLink(searchParams.get('lead'));
      const next = new URLSearchParams(searchParams);
      next.delete('create');
      next.delete('lead');
      setSearchParams(next, { replace: true });
    }

    const pending = peekProjectCreateDeepLink();
    if (!pending) return;

    // Consume after a tick so Strict Mode remount can still see it
    const clearTimer = window.setTimeout(() => {
      clearProjectCreateDeepLink();
    }, 0);

    setCreatePrefillLead(null);
    setEditingProject(null);
    setCreateDefaultStatus(null);
    setIsModalOpen(true);

    const leadId = pending.leadId;
    if (leadId && !isOptimisticId(leadId)) {
      void leadsService.getById(leadId).then((lead) => {
        if (!lead) return;
        setCreatePrefillLead(lead);
        setLeads((prev) => upsertById(prev, lead));
      });
    }

    return () => window.clearTimeout(clearTimer);
  }, [searchParams, setSearchParams]);

  const handleSaveProject = async (projectData: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (editingProject) {
      // Update with optimistic UI (instant feedback)
      projectsCRUD.update(editingProject.id, { ...editingProject, ...projectData }, { previousItem: editingProject });
    } else {
      // Create with optimistic UI (instant feedback)
      if (isOptimisticId(projectData.leadId)) {
        showToast(t('projects.leadStillSaving'), 'error');
        return;
      }
      projectsCRUD.create(projectData);
      
      // Update lead status to 'in_production' when creating a new project
      if (projectData.leadId && !isOptimisticId(projectData.leadId)) {
        const lead = leads.find(l => l.id === projectData.leadId);
        if (lead && lead.status !== 'in_production') {
          await leadsService.update(projectData.leadId, { 
            ...lead, 
            status: 'in_production',
            lastActionAt: new Date()
          });
        }
      }
    }
  };

  const handleDeleteProject = (id: string) => {
    // Fire-and-forget so ActionIconButton does not keep a spinner during confirm
    void (async () => {
      const projectToDelete = allProjects.find(p => p.id === id);
      if (!projectToDelete) return;

      let linkedPayments = payments.filter((p) => p.projectId === id);
      if (!paymentsKnownProjectIdsRef.current.has(id) && !isOptimisticId(id)) {
        try {
          const fetched = await paymentsService.getByProjectIds([id]);
          paymentsKnownProjectIdsRef.current.add(id);
          if (fetched.length > 0) {
            linkedPayments = fetched;
            setPayments((prev) => {
              const map = new Map(prev.map((p) => [p.id, p]));
              fetched.forEach((p) => map.set(p.id, p));
              return Array.from(map.values());
            });
          }
        } catch {
          // Fall through with local count
        }
      }

      if (linkedPayments.length > 0) {
        const confirmed = await confirm({
          title: t('projects.confirmDeleteWithPaymentsTitle'),
          message: t('projects.confirmDeleteWithPayments', { count: linkedPayments.length }),
          confirmText: t('common.delete'),
          variant: 'danger',
        });
        if (!confirmed) return;
      }

      // Store the project data for undo
      const projectData = { ...projectToDelete };

      // Delete with optimistic UI (instant feedback)
      projectsCRUD.remove(id);
      if (linkedPayments.length > 0) {
        const removedIds = new Set(linkedPayments.map((p) => p.id));
        setPayments((prev) => prev.filter((p) => !removedIds.has(p.id)));
        notifyPaymentMutated();
      }

      // Show toast with undo immediately
      showToastWithUndo(
        t('projects.deleted', { name: projectToDelete.name }),
        async () => {
          // Undo: recreate the project (payments / files are not restored)
          const result = await projectsService.create({
            name: projectData.name,
            leadId: projectData.leadId,
            type: projectData.type,
            status: projectData.status,
            brief: projectData.brief,
            amount: projectData.amount,
            currency: projectData.currency,
            deadline: projectData.deadline,
            deliverables: projectData.deliverables,
            files: projectData.files,
            rights: projectData.rights,
          });
          if (result && !(result as any).error && (result as Project).id) {
            addPaginatedProject(result as Project);
          }
        },
        'error'
      );
    })();
  };


  const handleUploadFile = (project: Project) => {
    // For now, open edit modal
    setEditingProject(project);
    setIsModalOpen(true);
  };

  const handleMoveToNextStatus = async (project: Project) => {
    const { getNextProjectStatus } = await import('../../utils/statusUtils');
    const nextStatus = getNextProjectStatus(project.status);
    if (nextStatus) {
      // Update with optimistic UI (instant feedback)
      projectsCRUD.update(project.id, { ...project, status: nextStatus }, { previousItem: project });
    }
  };

  const handleUpdateProjectStatus = async (projectId: string, newStatus: ProjectStatus) => {
    const project = allProjects.find(p => p.id === projectId);
    if (!project) return;

    // Update with optimistic UI (instant feedback)
    projectsCRUD.update(projectId, { ...project, status: newStatus }, { previousItem: project });
  };

  const handleMarkComplete = (project: Project) => {
    // Update with optimistic UI (instant feedback)
    projectsCRUD.update(project.id, { ...project, status: 'DELIVERED' }, { previousItem: project });
  };

  const handleStartDiffusion = (project: Project) => {
    if (isOptimisticId(project.id)) {
      showToast(t('projects.saveProjectFirst'), 'warning');
      return;
    }
    if (!project.rights) {
      showToast(t('projects.assignmentRights'), 'warning');
      return;
    }
    setDiffusionProject(project);
  };

  const handleConfirmDiffusionStarted = (payload: {
    startDate: Date;
    platforms: string[];
    duration: number;
    durationUnit: DurationUnit;
    durationValue: number;
    territory: string;
    exclusivity: boolean;
  }) => {
    if (!diffusionProject) return;
    const endDate = addDurationToLocalDate(
      payload.startDate,
      payload.durationValue,
      payload.durationUnit
    );
    const previousItem = diffusionProject;
    const updated: Project = {
      ...diffusionProject,
      rights: {
        id: diffusionProject.rights?.id || '',
        projectId: diffusionProject.id,
        platforms: payload.platforms,
        duration: payload.duration,
        territory: payload.territory,
        exclusivity: payload.exclusivity,
        startDate: payload.startDate,
        endDate,
        checkedAt: null,
        extensions: diffusionProject.rights?.extensions || [],
      },
    };
    projectsCRUD.update(diffusionProject.id, updated, { previousItem });
    setDiffusionProject(null);
    showToast(
      t('projects.diffusionStartedSuccess', { date: formatDateLocale(endDate) }),
      'success'
    );
  };

  const handleCreatePayment = (project: Project) => {
    if (isOptimisticId(project.id)) {
      showToast(t('projects.saveProjectFirst'), 'warning');
      return;
    }
    navigate(
      `/app/finances?createPayment=true&projectId=${encodeURIComponent(project.id)}`
    );
  };

  const handleMarkPaymentAsPaid = (payment: Payment) => {
    setMarkingAsPaidPayment(payment);
    setIsMarkAsPaidModalOpen(true);
  };

  const handleConfirmMarkAsPaid = async (paymentDate: Date, amountBaseCurrency: number) => {
    if (!markingAsPaidPayment) return;
    
    try {
      const paymentId = markingAsPaidPayment.id;
      const seq = (paymentMutSeqRef.current[paymentId] ?? 0) + 1;
      paymentMutSeqRef.current[paymentId] = seq;

      // Capture previous payment for rollback
      const previous = payments.find(p => p.id === paymentId);

      const nextAmountBase = revenueAmountForMarkAsPaid(markingAsPaidPayment, amountBaseCurrency);

      // Optimistic UI: mark as paid immediately
      setPayments(prev => prev.map(p => p.id === paymentId ? { ...p, status: 'paid', paymentDate, amountBaseCurrency: nextAmountBase, updatedAt: new Date() } : p));
      setMarkingAsPaidPayment(prev => prev ? { ...prev, status: 'paid', paymentDate, amountBaseCurrency: nextAmountBase } : prev);

      const result = await paymentsService.markAsPaid(paymentId, paymentDate, amountBaseCurrency);
      if (!result) {
        // Rollback only if still latest mutation for this payment
        if ((paymentMutSeqRef.current[paymentId] ?? 0) === seq && previous) {
          setPayments(prev => prev.map(p => p.id === paymentId ? previous : p));
          setMarkingAsPaidPayment(previous);
        }
        return;
      }

      // Success: update with server response and close modal
      setPayments(prev => prev.map(p => p.id === paymentId ? result : p));
      notifyPaymentMutated();
      setIsMarkAsPaidModalOpen(false);
      setMarkingAsPaidPayment(null);
    } catch (error) {
      console.error('Error marking payment as paid:', error);
    }
  };

  const handleViewPayment = (payment: Payment) => {
    // Navigate to Finances page with payment selected
    navigate(`/app/finances?paymentId=${payment.id}`);
  };

  const handleViewLead = (leadId: string) => {
    // Navigate to Leads page with lead selected
    navigate(`/app/leads?lead=${leadId}`);
  };

  const handleExport = async () => {
    try {
      // Always fetch ALL projects directly from service to ensure we get everything
      const allProjectsFromService = await projectsService.getAll();
      
      if (allProjectsFromService.length === 0) {
        showToast(t('projects.noProjectsToExport'), 'warning');
        return;
      }

      // Fetch all leads referenced by these projects (avoid relying on partial in-memory cache)
      const exportLeadIds = Array.from(
        new Set(allProjectsFromService.map((p) => p.leadId).filter(Boolean))
      );
      const exportLeads = await leadsService.getByIds(exportLeadIds);
      const exportLeadsById = new Map(exportLeads.map((l) => [l.id, l]));
      
      // Apply same filters as table (search + all active filters with all operators)
      const searchLower = searchTerm.trim().toLowerCase();
      const filteredProjectsToExport = allProjectsFromService.filter((project) => {
        const lead = project.leadId ? exportLeadsById.get(project.leadId) : undefined;
        if (searchLower) {
          const projectNameMatch = project.name.toLowerCase().includes(searchLower);
          const leadNameMatch = (lead?.name ?? '').toLowerCase().includes(searchLower);
          if (!projectNameMatch && !leadNameMatch) return false;
        }
        return activeFilters.every((f) => projectMatchesFilter(project, lead, f, projectFilterFields));
      });

      if (filteredProjectsToExport.length === 0) {
        showToast(t('projects.noProjectsToExport'), 'warning');
        return;
      }

      const exportHeaders = [
        t('projects.name'),
        t('projects.lead'),
        t('projects.type'),
        t('projects.status'),
        t('projects.amount'),
        t('projects.currency'),
        t('projects.deadline'),
        t('projects.description'),
        t('projects.deliverables'),
        t('leads.createdAt')
      ];

      const exportData = filteredProjectsToExport.map((project) => {
        const lead = project.leadId ? exportLeadsById.get(project.leadId) : undefined;
        return {
          [t('projects.name')]: project.name,
          [t('projects.lead')]: lead?.name || t('projects.unknownLead'),
          [t('projects.type')]: project.type,
          [t('projects.status')]: project.status,
          [t('projects.amount')]: project.amount,
          [t('projects.currency')]: project.currency,
          [t('projects.deadline')]: project.deadline != null ? formatDateTimeForExport(project.deadline) : '',
          [t('projects.description')]: project.brief || '',
          [t('projects.deliverables')]: project.deliverables.join(', '),
          [t('leads.createdAt')]: formatDateTimeForExport(project.createdAt)
        };
      });

      exportToCSV(exportData, `projets-${new Date().toISOString().split('T')[0]}`, exportHeaders);
      showToast(t('common.exportSuccess', { count: exportData.length }) || `${exportData.length} projets exportés`, 'success');
    } catch (error) {
      console.error('Error exporting projects:', error);
      showToast(t('projects.exportError') || 'Erreur lors de l\'export des projets', 'error');
    }
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-500 text-lg font-medium mb-2">Error</div>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`w-full flex flex-col ${PAGE_GAP} ${
        kanbanPageScroll ? 'min-h-full' : 'flex-1 min-h-0 h-full'
      }`}
    >
      {/* Header */}
      <PageHeader
        title={t('projects.title')}
        description={t('projects.manageProjectsDescription')}
        actionsConfig={{
          newButtonKey: 'projects.newProject',
          onCreateNew: () => handleOpenModal(),
          onExport: handleExport,
          hideNewOnMobile: true,
        }}
      />

      {/* Stats removed on non-overview pages */}

      {/* Table Section with Actions and Filters */}
      <TableSection
        actionsConfig={{}}
        className={kanbanPageScroll ? '' : 'flex-1 min-h-0'}
        filters={
          <TableFilters
            searchConfig={{
              value: searchTerm,
              onChange: setSearchTerm,
              placeholder: t('projects.searchProject'),
            }}
            filterFields={projectFilterFields}
            activeFilters={activeFilters}
            onChange={setActiveFilters}
            mobilePrimaryAction={
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleOpenModal()}
                icon={<Plus className="w-4 h-4" />}
              >
                {t('common.new')}
              </Button>
            }
          />
        }
      >
        {/* Projets */}
        <div
          id="projects-container"
          ref={projectsLayoutRef}
          className={
            kanbanPageScroll
              ? 'relative'
              : 'relative h-full flex-1 min-h-0'
          }
        >
          {view === 'kanban' ? (
            <>
              {loading && allProjects.length === 0 ? (
                <Card>
                  <div className="py-16 flex justify-center">
                    <Spinner size="lg" label={t('common.loading')} />
                  </div>
                </Card>
              ) : (
                <KanbanBoard
                  projects={sortedProjects}
                  archivedCount={archivedProjects.length}
                  leads={leads}
                  payments={payments}
                  highlightedProjectId={highlightedProjectId}
                  filterKey={`${searchTerm}\0${activeFilters.map((f) => `${f.id}:${f.operator}:${f.value}`).join('|')}`}
                  layoutWidth={projectsLayoutWidth}
                  pageScroll={kanbanPageScroll}
                  compactFocusStatus={compactFocusStatus}
                  onCompactFocusStatusApplied={() => setCompactFocusStatus(null)}
                  onEdit={handleOpenModal}
                  onDelete={handleDeleteProject}
                  onUploadFile={handleUploadFile}
                  onMarkComplete={handleMarkComplete}
                  onCreatePayment={handleCreatePayment}
                  onViewPayment={handleViewPayment}
                  onMarkPaymentAsPaid={handleMarkPaymentAsPaid}
                  onViewLead={handleViewLead}
                  onMoveToNextStatus={handleMoveToNextStatus}
                  onUpdateStatus={handleUpdateProjectStatus}
                  onViewArchived={() => setView('archived')}
                  onStartDiffusion={handleStartDiffusion}
                  onCreateInColumn={handleCreateInColumn}
                />
              )}
            </>
          ) : (
            <>
              <BulkActionsBar {...archivedBulk.bulkBarProps} />
              <div className="flex h-full min-h-0 flex-1 flex-col gap-2 overflow-hidden">
                {/* Compact: same status menu as kanban — no desktop back column */}
                {!archivedIsFullBoard ? (
                  <div className="flex-shrink-0">
                    <KanbanCompactNav
                      counts={kanbanStatusCounts}
                      archivedCount={archivedProjects.length}
                      selected="ARCHIVED"
                      showLabels={archivedShowChipLabels}
                      onSelectStatus={(status) => {
                        setCompactFocusStatus(status);
                        setView('kanban');
                      }}
                      onSelectArchived={() => setView('archived')}
                    />
                  </div>
                ) : null}

                <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
                  <div className="flex min-h-full min-w-0 w-full max-w-full space-x-3 px-0 sm:space-x-4 lg:px-6">
                    {archivedIsFullBoard ? (
                      <BackToKanbanColumn
                        activeCount={sortedProjects.length || 0}
                        onClick={() => setView('kanban')}
                        ariaLabel={t('projects.backToKanban')}
                      />
                    ) : null}

                    {/* Table des projets archivés */}
                    <div className="min-w-0 flex-1 space-y-4">
                    {archivedProjects.length === 0 ? (
                      <div className="border border-gray-200/60 rounded-xl shadow-sm bg-white overflow-hidden">
                        <div className="text-center py-16">
                          <Archive className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('projects.noArchivedProjects') || 'Aucun projet archivé'}</h3>
                          <p className="text-gray-500 text-sm">{t('projects.noArchivedProjectsDescription') || 'Les projets archivés apparaîtront ici.'}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="border border-gray-200/60 rounded-xl shadow-sm bg-white overflow-hidden">
                        <Table className="border-0 rounded-none" dataTableColumnCount={8} disableOverflowX>
                          <TableHeader>
                            <TableRow>
                              <TableSelectionHeader
                                isAllSelected={archivedSelection.isAllSelected}
                                selectedCount={archivedSelection.selectedCount}
                                onSelectAll={archivedSelection.handleSelectAll}
                                selectAllAriaLabel={t('projects.selectAll')}
                              />
                              <TableHead>{t('projects.name') || 'Nom'}</TableHead>
                              <TableHead className="hidden sm:table-cell">{t('projects.lead') || 'Lead'}</TableHead>
                              <TableHead className="hidden lg:table-cell">{t('projects.type') || 'Type'}</TableHead>
                              <TableHead className="hidden md:table-cell">{t('projects.amount') || 'Montant'}</TableHead>
                              <TableHead className="hidden lg:table-cell">{t('projects.deadline') || 'Date limite'}</TableHead>
                              <TableHead className="hidden md:table-cell">{t('projects.rightsEndColumn')}</TableHead>
                              <TableActionsHeader label={t('common.actions') || 'Actions'} />
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {archivedProjects.map((project) => {
                              const lead = leads.find(p => p.id === project.leadId);
                              const isRowSelected = archivedSelection.isSelected(project.id);

                              return (
                                <TableRow
                                  key={project.id}
                                  data-project-id={project.id}
                                  onClick={() => handleOpenModal(project)}
                                  className={`opacity-75 rounded-lg transition-all duration-300 ${
                                    isRowSelected ? tableRowSelectedClass : ''
                                  } ${project.id === highlightedProjectId ? highlightRing.combinedWithShadow : ''}`}
                                >
                                  <TableSelectionCell
                                    itemId={project.id}
                                    isSelected={archivedSelection.isSelected(project.id)}
                                    onToggle={archivedSelection.handleSelectItem}
                                  />
                                  <TableCell className="font-medium">{project.name}</TableCell>
                                  <TableCell className="hidden sm:table-cell">
                                    {lead ? (
                                      handleViewLead ? (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleViewLead(lead.id);
                                          }}
                                          className="text-blue-600 hover:underline"
                                        >
                                          {lead.name}
                                        </button>
                                      ) : (
                                        <span>{lead.name}</span>
                                      )
                                    ) : (
                                      <span className="text-gray-400">{t('projects.unknownLead')}</span>
                                    )}
                                  </TableCell>
                                  <TableCell className="hidden lg:table-cell">
                                    <div className="flex items-center gap-1.5 min-w-0">
                                      <span className="text-gray-400 shrink-0">{PROJECT_TYPE_ICONS[project.type] || '📄'}</span>
                                      <span className="truncate">{getProjectTypeLabel(project.type)}</span>
                                    </div>
                                  </TableCell>
                                  <TableCell className="hidden md:table-cell">
                                    {project.amount ? (
                                      <span className="font-medium">{formatCurrency(project.amount, project.currency || 'EUR')}</span>
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </TableCell>
                                  <TableCell className="hidden lg:table-cell">
                                    {project.deadline ? (
                                      <span>{formatDateShort(project.deadline)}</span>
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </TableCell>
                                  <TableCell className="hidden md:table-cell">
                                    {project.rights?.endDate ? (
                                      <RightsEndStatus
                                        endDate={project.rights.endDate}
                                        checkedAt={project.rights.checkedAt}
                                        compact
                                      />
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </TableCell>
                                  <TableActionsCell>
                                    {(() => {
                                      const menuItems: ContextMenuItem[] = [];
                                      appendEditDeleteMenuItems({
                                        items: menuItems,
                                        t,
                                        onEdit: () => handleOpenModal(project),
                                        onDelete: () => handleDeleteProject(project.id),
                                        omitFromMenu: showQuickEditDelete,
                                      });
                                      return menuItems.length > 0 ? (
                                        <ContextMenu
                                          items={menuItems}
                                          trigger="more"
                                          size="sm"
                                          tooltip={t('common.moreActions')}
                                        />
                                      ) : null;
                                    })()}
                                    <span className={TABLE_QUICK_ACTION_CLASS}>
                                      <ActionIconButton
                                        icon={Edit}
                                        onClick={() => handleOpenModal(project)}
                                        variant="edit"
                                        size="sm"
                                        tooltip={t('common.edit')}
                                      />
                                    </span>
                                    <span className={TABLE_QUICK_ACTION_CLASS}>
                                      <ActionIconButton
                                        icon={Trash2}
                                        onClick={() => handleDeleteProject(project.id)}
                                        variant="delete"
                                        size="sm"
                                        tooltip={t('common.delete')}
                                      />
                                    </span>
                                  </TableActionsCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                        <InfiniteLoadMore
                          hasMore={hasMore}
                          loading={loadingMore}
                          hasItems={archivedProjects.length > 0}
                          onLoadMore={loadMore}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              </div>
            </>
          )}
        </div>
      </TableSection>

      {/* Bulk delete confirmation (archived projects) */}
      <ConfirmModal
        isOpen={archivedBulk.bulkDeleteModal.isOpen}
        onClose={archivedBulk.bulkDeleteModal.onClose}
        onConfirm={archivedBulk.bulkDeleteModal.onConfirm}
        title={t('common.confirm')}
        message={
          bulkDeleteLinkedPaymentCount > 0
            ? t('projects.confirmBulkDeleteWithPayments', {
                count: archivedBulk.bulkDeleteModal.pendingCount,
                paymentCount: bulkDeleteLinkedPaymentCount,
              })
            : t('projects.confirmBulkDelete', { count: archivedBulk.bulkDeleteModal.pendingCount })
        }
        variant="danger"
      />

      {/* Modals */}
      <ProjectModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveProject}
        project={editingProject}
        leads={
          editingProject?.leadId && leadsById.get(editingProject.leadId)
            ? [leadsById.get(editingProject.leadId)!]
            : createPrefillLead
              ? [createPrefillLead]
              : []
        }
        searchLeadsFn={(term) => leadsService.search(term, 10)}
        preselectedLead={
          editingProject?.leadId
            ? leadsById.get(editingProject.leadId) || null
            : createPrefillLead
        }
        onCreateLead={handleCreateLeadFromSearch}
        defaultStatus={createDefaultStatus || undefined}
      />

      {leadCreateModal}

      {/* Mark as Paid Modal */}
      <MarkAsPaidModal
        isOpen={isMarkAsPaidModalOpen}
        onClose={() => {
          setIsMarkAsPaidModalOpen(false);
          setMarkingAsPaidPayment(null);
        }}
        onConfirm={handleConfirmMarkAsPaid}
        payment={markingAsPaidPayment}
      />

      <DiffusionStartedModal
        isOpen={!!diffusionProject}
        onClose={() => setDiffusionProject(null)}
        onConfirm={handleConfirmDiffusionStarted}
        project={diffusionProject}
      />

    </div>
  );
};

export default Projects;
